import React, { useRef, useEffect, useState } from 'react';
import { Star, Quote } from 'lucide-react';
import { reviews } from '../mock';
import Reveal from './Reveal';

const ReviewCard = ({ r }) => (
  <div className="shrink-0 w-[320px] md:w-[400px] rounded-2xl bg-[#353539] border border-white/5 p-7 md:p-8 mx-2.5 flex flex-col select-none">
    <div className="flex items-center justify-between mb-5">
      <div className="flex gap-1">
        {Array.from({ length: r.rating }).map((_, i) => (
          <Star key={i} className="w-5 h-5 text-yellow" fill="#ff7a1a" strokeWidth={0} />
        ))}
      </div>
      <Quote className="w-8 h-8 text-yellow/40" fill="currentColor" strokeWidth={0} />
    </div>
    <p className="text-white/90 text-base md:text-lg leading-relaxed flex-1">{r.text}</p>
    <div className="mt-6 pt-5 border-t border-white/10">
      <p className="font-display text-white text-2xl md:text-3xl leading-none">{r.name}</p>
    </div>
  </div>
);

const Reviews = () => {
  const doubled = [...reviews, ...reviews];
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const drag = useRef({ active: false, startX: 0, startScroll: 0, moved: 0 });
  const paused = useRef(false);

  // auto-scroll loop (paused while hovering or dragging); loops seamlessly using the doubled list
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf;
    const step = () => {
      if (el && !paused.current && !drag.current.active) {
        el.scrollLeft += 0.6;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft -= el.scrollWidth / 2;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onPointerDown = (e) => {
    const el = trackRef.current;
    if (!el) return;
    drag.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: 0 };
    setDragging(true);
    el.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const el = trackRef.current;
    const dx = e.clientX - drag.current.startX;
    drag.current.moved = Math.abs(dx);
    el.scrollLeft = drag.current.startScroll - dx;
    if (el.scrollLeft <= 0) el.scrollLeft += el.scrollWidth / 2;
    else if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft -= el.scrollWidth / 2;
  };
  const endDrag = () => {
    drag.current.active = false;
    setDragging(false);
  };

  return (
    <section id="reviews" className="bg-[#262629] py-24">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <h2 className="font-display text-yellow text-4xl md:text-5xl mb-5">What our builders say</h2>
              <p className="text-white/80 text-base md:text-lg leading-relaxed">
                We work with builders and developers right across the UK. Here's what they have to say about faster tenders, tighter margins and estimates they can trust.
              </p>
            </div>
            <a href="/reviews" className="shrink-0 bg-jaw-yellow text-[#262629] font-semibold px-8 py-4 rounded-[10px] hover:bg-[#e8660a] transition-colors">
              Read more reviews
            </a>
          </div>
        </Reveal>

        {/* draggable auto-scrolling row (no visible scrollbar) with edge fades */}
        <Reveal delay={0.1}>
          <div className="relative">
            <div
              ref={trackRef}
              className={`no-scrollbar drag-row flex overflow-x-auto items-stretch ${dragging ? 'dragging' : ''}`}
              style={{ touchAction: 'pan-y' }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onClickCapture={(e) => { if (drag.current.moved > 6) { e.preventDefault(); e.stopPropagation(); } }}
            >
              {doubled.map((r, i) => (
                <ReviewCard key={i} r={r} />
              ))}
            </div>
            {/* left/right fade masks */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-12 md:w-28 bg-gradient-to-r from-[#262629] to-transparent z-10" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-12 md:w-28 bg-gradient-to-l from-[#262629] to-transparent z-10" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Reviews;
